import { useState, useEffect } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import {
  Trophy,
  XCircle,
  CheckCircle2,
  RotateCcw,
  LayoutDashboard,
  Loader2,
  AlertCircle,
  Target,
  Sparkles,
  CircleHelp,
} from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useLanguage } from "@/hooks/use-language";
import { getQuestionText, getChoiceText, getExplanation } from "@/lib/localize";
import { getTestSession, generateExplanation, createTest } from "@/lib/api";
import { createErrorMessage } from "@/lib/error-message";
import type { TestSession, UserAnswer, Question } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Markdown } from "@/components/ui/markdown";

export const Route = createFileRoute("/_main/exams/$slug/test/$id/result")({
  component: TestResultPage,
});

// ─── Types ──────────────────────────────────────────────────────────────────

interface StoredTestData {
  session: TestSession;
  questions: Question[];
}

interface StoredAnswersData {
  answers: Record<
    string,
    {
      selectedLabels: string[];
      submitted: boolean;
      result: UserAnswer | null;
      timeSpentMs: number;
      isAmbiguous?: boolean;
    }
  >;
  currentIndex: number;
}

interface ReviewItem {
  question: Question;
  userLabels: string[];
  correctLabels: string[];
  isCorrect: boolean;
  timeSpentMs: number;
  isAmbiguous: boolean;
}

// ─── Component ──────────────────────────────────────────────────────────────

const PASS_THRESHOLD = 70;

export function TestResultPage() {
  const params = useParams({ strict: false }) as { slug: string; id: string };
  const examSlug = params.slug;
  const testId = params.id;
  const navigate = useNavigate();
  const { loading: authLoading } = useAuth();
  const { t, locale } = useLanguage();

  const [session, setSession] = useState<TestSession | null>(null);
  const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
  const [wrongQuestionIds, setWrongQuestionIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingFor, setGeneratingFor] = useState<number | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [retryingAll, setRetryingAll] = useState(false);

  // Load result data
  useEffect(() => {
    async function loadResults() {
      setLoading(true);
      setError(null);

      try {
        // 1. Fetch session + answers + questions from API
        const { session: apiSession, answers: apiAnswers, questions: apiQuestions } = await getTestSession(examSlug, testId);
        setSession(apiSession);

        // Always track wrong question IDs from API (works even without localStorage)
        setWrongQuestionIds(
          apiAnswers.filter((a) => !a.is_correct).map((a) => a.question_id)
        );

        // 2. Try to load questions from localStorage (preferred for isAmbiguous state)
        const testRaw = localStorage.getItem(`test_${testId}`);
        const answersRaw = localStorage.getItem(`test_answers_${testId}`);

        const testData: StoredTestData | null = testRaw ? JSON.parse(testRaw) : null;
        const answersData: StoredAnswersData | null = answersRaw
          ? JSON.parse(answersRaw)
          : null;

        // Use localStorage questions if available, otherwise fall back to API questions
        const questions = testData?.questions ?? apiQuestions ?? [];

        if (questions.length > 0) {
          // Build a map of API answers by question_id for quick lookup
          const apiAnswerMap = new Map<number, UserAnswer>();
          for (const ans of apiAnswers) {
            apiAnswerMap.set(ans.question_id, ans);
          }

          const items: ReviewItem[] = questions.map((q) => {
            const localAnswer = answersData?.answers[String(q.id)];
            const serverAnswer = apiAnswerMap.get(q.id);
            const correctLabels =
              q.choices
                ?.filter((c) => c.is_correct)
                .map((c) => c.label) ?? [];

            return {
              question: q,
              userLabels: serverAnswer?.selected_labels ?? localAnswer?.selectedLabels ?? [],
              correctLabels,
              isCorrect: serverAnswer?.is_correct ?? localAnswer?.result?.is_correct ?? false,
              timeSpentMs: serverAnswer?.time_spent_ms ?? localAnswer?.timeSpentMs ?? 0,
              isAmbiguous: localAnswer?.isAmbiguous ?? false,
            };
          });

          setReviewItems(items);

          // Save sequential progress if tagged (namespaced by exam)
          try {
            const metaRaw = localStorage.getItem(`test_meta_${testId}`);
            if (metaRaw) {
              const meta = JSON.parse(metaRaw);
              if (meta.type === "sequential" && typeof meta.setIndex === "number") {
                const correctCount = items.filter((i) => i.isCorrect).length;
                const total = items.length;
                const progressKey = meta.examSlug
                  ? `sequential_progress_${meta.examSlug}`
                  : `sequential_progress_${examSlug}`;
                const progressRaw = localStorage.getItem(progressKey);
                const progress: Record<string, { score: number; total: number; date: string }> =
                  progressRaw ? JSON.parse(progressRaw) : {};
                progress[String(meta.setIndex)] = {
                  score: correctCount,
                  total,
                  date: new Date().toISOString(),
                };
                localStorage.setItem(progressKey, JSON.stringify(progress));
              }
              localStorage.removeItem(`test_meta_${testId}`);
            }
          } catch {
            // ignore progress save errors
          }

          // Clean up localStorage now that we've loaded the data
          localStorage.removeItem(`test_${testId}`);
          localStorage.removeItem(`test_answers_${testId}`);
        }
      } catch (err) {
        setError(createErrorMessage(err, "결과를 불러오는 중 오류가 발생했습니다."));
      } finally {
        setLoading(false);
      }
    }

    if (!authLoading) {
      loadResults();
    }
  }, [testId, examSlug, authLoading]);

  async function handleGenerateExplanation(questionId: number) {
    setGeneratingFor(questionId);
    try {
      const updated = await generateExplanation(examSlug, questionId);
      if (updated.explanation) {
        setReviewItems((prev) =>
          prev.map((item) =>
            item.question.id === questionId
              ? { ...item, question: { ...item.question, explanation: updated.explanation } }
              : item,
          ),
        );
      }
    } catch {
      // silently fail
    } finally {
      setGeneratingFor(null);
    }
  }

  const wrongItems = reviewItems.filter((r) => !r.isCorrect);
  const ambiguousItems = reviewItems.filter((r) => r.isAmbiguous);

  // Use reviewItems wrong IDs if available, otherwise fall back to API-derived IDs
  const effectiveWrongIds =
    wrongItems.length > 0
      ? wrongItems.map((r) => r.question.id)
      : wrongQuestionIds;
  const wrongCount = effectiveWrongIds.length;

  async function handleRetryWrong() {
    if (effectiveWrongIds.length === 0) return;
    setRetrying(true);
    try {
      const { session: newSession, questions } = await createTest(examSlug, {
        question_ids: effectiveWrongIds,
        mode: "practice",
      });
      localStorage.setItem(
        `test_${newSession.id}`,
        JSON.stringify({ session: newSession, questions }),
      );
      navigate({
        to: "/exams/$slug/test/$id",
        params: { slug: examSlug, id: newSession.id },
      });
    } catch {
      setRetrying(false);
    }
  }

  const [retryingAmbiguous, setRetryingAmbiguous] = useState(false);

  async function handleRetryAmbiguous() {
    if (ambiguousItems.length === 0) return;
    setRetryingAmbiguous(true);
    try {
      const ids = ambiguousItems.map((r) => r.question.id);
      const { session: newSession, questions } = await createTest(examSlug, {
        question_ids: ids,
        mode: "practice",
      });
      localStorage.setItem(
        `test_${newSession.id}`,
        JSON.stringify({ session: newSession, questions }),
      );
      navigate({
        to: "/exams/$slug/test/$id",
        params: { slug: examSlug, id: newSession.id },
      });
    } catch {
      setRetryingAmbiguous(false);
    }
  }

  async function handleRetryAll() {
    if (reviewItems.length === 0) return;
    setRetryingAll(true);
    try {
      const allIds = reviewItems.map((r) => r.question.id);
      const { session: newSession, questions } = await createTest(examSlug, {
        question_ids: allIds,
        mode: "practice",
      });
      localStorage.setItem(
        `test_${newSession.id}`,
        JSON.stringify({ session: newSession, questions }),
      );
      navigate({
        to: "/exams/$slug/test/$id",
        params: { slug: examSlug, id: newSession.id },
      });
    } catch {
      setRetryingAll(false);
    }
  }

  // ─── Computed ─────────────────────────────────────────────────────────────

  const totalQuestions = session?.total_questions ?? reviewItems.length;
  const correctCount = session?.correct_count ?? reviewItems.filter((r) => r.isCorrect).length;
  const percentage =
    totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
  const passed = percentage >= PASS_THRESHOLD;
  const totalTimeMs = reviewItems.reduce((sum, r) => sum + r.timeSpentMs, 0);

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading || authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (error && !session) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-4">
        <AlertCircle className="h-12 w-12 text-zinc-400" />
        <p className="text-center text-zinc-600 dark:text-zinc-400">{error}</p>
        <Button
          onClick={() =>
            navigate({
              to: "/exams/$slug/test/new",
              params: { slug: examSlug },
            })
          }
        >
          {t("result.new_test")}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      <div className="mx-auto max-w-3xl px-4 py-6 sm:px-6 lg:px-8">
        {/* Score Summary */}
        <Card className="mb-6 overflow-hidden">
          <div
            className={`px-6 py-8 text-center ${
              passed
                ? "bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950"
                : "bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950 dark:to-orange-950"
            }`}
          >
            {/* Pass/Fail Icon */}
            <div className="mb-4 flex justify-center">
              {passed ? (
                <div className="rounded-full bg-green-100 p-4 dark:bg-green-900">
                  <Trophy className="h-10 w-10 text-green-600 dark:text-green-400" />
                </div>
              ) : (
                <div className="rounded-full bg-red-100 p-4 dark:bg-red-900">
                  <Target className="h-10 w-10 text-red-600 dark:text-red-400" />
                </div>
              )}
            </div>

            {/* Large Score */}
            <div className="mb-2">
              <span
                className={`text-6xl font-extrabold sm:text-7xl ${
                  passed
                    ? "text-green-600 dark:text-green-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {percentage}
              </span>
              <span
                className={`text-2xl font-bold ${
                  passed
                    ? "text-green-500 dark:text-green-500"
                    : "text-red-500 dark:text-red-500"
                }`}
              >
                %
              </span>
            </div>

            {/* Pass/Fail Badge */}
            <Badge
              className={`text-sm px-4 py-1 ${
                passed
                  ? "bg-green-600 text-white hover:bg-green-700"
                  : "bg-red-600 text-white hover:bg-red-700"
              }`}
            >
              {passed ? t("common.pass") : t("common.fail")}
            </Badge>

            {/* Details */}
            <div className="mt-4 flex items-center justify-center gap-6 text-sm">
              <div className="text-center">
                <p
                  className={`text-2xl font-bold ${
                    passed
                      ? "text-green-700 dark:text-green-300"
                      : "text-red-700 dark:text-red-300"
                  }`}
                >
                  {correctCount}
                </p>
                <p className="text-zinc-500">{t("result.correct_count")}</p>
              </div>
              <Separator orientation="vertical" className="h-10" />
              <div className="text-center">
                <p className="text-2xl font-bold text-zinc-700 dark:text-zinc-300">
                  {totalQuestions}
                </p>
                <p className="text-zinc-500">{t("result.total_count")}</p>
              </div>
              <Separator orientation="vertical" className="h-10" />
              <div className="text-center">
                <p className="text-2xl font-bold text-zinc-700 dark:text-zinc-300">
                  {totalTimeMs > 0
                    ? `${Math.floor(totalTimeMs / 60000)}${t("common.minutes_suffix")}`
                    : "-"}
                </p>
                <p className="text-zinc-500">{t("result.time_spent")}</p>
              </div>
            </div>
          </div>

          {/* Progress bar */}
          <CardContent className="py-4">
            <div className="flex items-center justify-between text-xs text-zinc-500 mb-1">
              <span>{t("result.accuracy_rate")}</span>
              <span>{t("result.pass_criteria")}: {PASS_THRESHOLD}%</span>
            </div>
            <div className="relative">
              <Progress value={percentage} className="h-3" />
              {/* Threshold marker */}
              <div
                className="absolute top-0 h-3 w-0.5 bg-zinc-800 dark:bg-zinc-200"
                style={{ left: `${PASS_THRESHOLD}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="mb-8 grid grid-cols-2 gap-3">
          {wrongCount > 0 && (
            <Button
              variant="outline"
              className="col-span-2 h-12 min-h-[48px] border-red-200 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
              onClick={handleRetryWrong}
              disabled={retrying}
            >
              {retrying ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              {t("result.retry_wrong")} ({wrongCount}{t("common.question")})
            </Button>
          )}
          {ambiguousItems.length > 0 && (
            <Button
              variant="outline"
              className="col-span-2 h-12 min-h-[48px] border-amber-200 text-amber-600 hover:bg-amber-50 dark:border-amber-800 dark:text-amber-400 dark:hover:bg-amber-950"
              onClick={handleRetryAmbiguous}
              disabled={retryingAmbiguous}
            >
              {retryingAmbiguous ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CircleHelp className="mr-2 h-4 w-4" />
              )}
              {t("result.retry_unsure")} ({ambiguousItems.length}{t("common.question")})
            </Button>
          )}
          {reviewItems.length > 0 && (
            <Button
              variant="outline"
              className="col-span-2 h-12 min-h-[48px] border-blue-200 text-blue-600 hover:bg-blue-50 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-950"
              onClick={handleRetryAll}
              disabled={retryingAll}
            >
              {retryingAll ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              {t("result.retry_all")} ({reviewItems.length}{t("common.question")})
            </Button>
          )}
          <Button
            variant="outline"
            className="h-12 min-h-[48px]"
            onClick={() =>
              navigate({
                to: "/exams/$slug/test/new",
                params: { slug: examSlug },
              })
            }
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {t("result.new_test")}
          </Button>
          <Button
            className="h-12 min-h-[48px]"
            onClick={() =>
              navigate({
                to: "/exams/$slug",
                params: { slug: examSlug },
              })
            }
          >
            <LayoutDashboard className="mr-2 h-4 w-4" />
            {t("result.go_dashboard")}
          </Button>
        </div>

        {/* Answer Review */}
        {reviewItems.length > 0 && (
          <div>
            <h2 className="mb-4 text-lg font-bold text-zinc-900 dark:text-zinc-50">
              {t("result.question_results")}
            </h2>

            <div className="space-y-3">
              {reviewItems.map((item) => (
                <Card
                  key={item.question.id}
                  className={`border-l-4 ${
                    item.isCorrect
                      ? "border-l-green-500"
                      : "border-l-red-500"
                  }`}
                >
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="flex items-center gap-2 text-sm font-semibold">
                        {item.isCorrect ? (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        {t("common.question")} {item.question.display_number ?? item.question.question_number}
                        {item.isAmbiguous && (
                          <Badge
                            variant="outline"
                            className="text-xs border-amber-300 text-amber-600 dark:border-amber-700 dark:text-amber-400"
                          >
                            <CircleHelp className="mr-1 h-3 w-3" />
                            {t("test.unsure")}
                          </Badge>
                        )}
                      </CardTitle>
                      {item.timeSpentMs > 0 && (
                        <span className="text-xs text-zinc-400">
                          {Math.round(item.timeSpentMs / 1000)}{t("common.seconds_suffix")}
                        </span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {/* Question text preview */}
                    <p className="text-sm text-zinc-600 dark:text-zinc-400 line-clamp-2">
                      {getQuestionText(item.question, locale)}
                    </p>

                    {/* User answer vs correct answer */}
                    <div className="flex flex-col gap-1 text-sm sm:flex-row sm:gap-4">
                      <div className="flex items-center gap-1">
                        <span className="text-zinc-500">{t("test.my_answer")}:</span>
                        <div className="flex gap-1">
                          {item.userLabels.length > 0 ? (
                            item.userLabels.map((label) => (
                              <Badge
                                key={label}
                                variant={item.isCorrect ? "default" : "destructive"}
                                className="text-xs"
                              >
                                {label}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-zinc-400">{t("test.no_answer")}</span>
                          )}
                        </div>
                      </div>
                      {!item.isCorrect && (
                        <div className="flex items-center gap-1">
                          <span className="text-zinc-500">{t("common.correct")}:</span>
                          <div className="flex gap-1">
                            {item.correctLabels.map((label) => (
                              <Badge
                                key={label}
                                className="bg-green-600 text-xs text-white"
                              >
                                {label}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Choices detail for incorrect answers */}
                    {!item.isCorrect && item.question.choices && (
                      <div className="mt-2 space-y-1">
                        {item.question.choices.map((choice) => {
                          const wasSelected = item.userLabels.includes(
                            choice.label,
                          );
                          const isCorrectChoice = choice.is_correct;

                          let textColor =
                            "text-zinc-500 dark:text-zinc-500";
                          if (isCorrectChoice) {
                            textColor =
                              "text-green-700 font-medium dark:text-green-400";
                          } else if (wasSelected) {
                            textColor =
                              "text-red-600 line-through dark:text-red-400";
                          }

                          return (
                            <p
                              key={choice.label}
                              className={`text-xs ${textColor}`}
                            >
                              <span className="font-semibold">
                                {choice.label}.
                              </span>{" "}
                              {getChoiceText(choice, locale)}
                              {isCorrectChoice && " \u2713"}
                            </p>
                          );
                        })}
                      </div>
                    )}

                    {/* Explanation */}
                    {getExplanation(item.question, locale) ? (
                      <div className="mt-2 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-800 dark:bg-blue-950">
                        <p className="mb-1 text-xs font-medium text-blue-600 dark:text-blue-400">
                          {t("common.explanation")}
                        </p>
                        <div className="text-xs leading-relaxed text-blue-800 dark:text-blue-200">
                          <Markdown>{getExplanation(item.question, locale)!}</Markdown>
                        </div>
                      </div>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 h-8 text-xs text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-950"
                        onClick={() => handleGenerateExplanation(item.question.id)}
                        disabled={generatingFor === item.question.id}
                      >
                        {generatingFor === item.question.id ? (
                          <>
                            <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                            {t("test.generating")}
                          </>
                        ) : (
                          <>
                            <Sparkles className="mr-1 h-3 w-3" />
                            {t("test.ai_explanation_view")}
                          </>
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Bottom spacer for mobile */}
        <div className="h-8" />
      </div>
    </div>
  );
}
